import { useEffect } from "react";

import {
  ExperienceCategory,
  GetExperiencesListResponseBodyType,
} from "@intelligems/ig-types";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";
import { experienceKeys, historyKeys } from "../queryKeys";

const getExperiencesList = async (options: {
  category: ExperienceCategory;
  page?: number;
  limit?: number;
}) => {
  const resp = await api.get<GetExperiencesListResponseBodyType>(
    `/v2/experiences-list`,
    {
      params: {
        page: options.page,
        limit: options.limit,
        category: options.category,
      },
    }
  );
  return resp.data;
};

export const useGetExperiencesList = <T extends ExperienceCategory>({
  category,
}: {
  category: T;
}) => {
  const queryClient = useQueryClient();
  const limit = 50;
  const experiences = useInfiniteQuery<
    GetExperiencesListResponseBodyType,
    Error,
    GetExperiencesListResponseBodyType
  >({
    initialPageParam: 1,
    queryKey:
      category === "experiment"
        ? experienceKeys.experimentList
        : experienceKeys.personalizationList,
    queryFn: async ({ pageParam = 1 }: { pageParam: unknown }) =>
      getExperiencesList({ category, page: pageParam as number, limit }),
    getNextPageParam: (lastPage) => {
      if (lastPage.experiencesList.length < limit) {
        return undefined;
      }
      return lastPage.page ? lastPage.page + 1 : undefined;
    },
    getPreviousPageParam: (firstPage) => {
      if (firstPage.page && firstPage.page > 1) {
        return firstPage.page - 1;
      }
      return undefined;
    },
    select: (data) => {
      return {
        experiencesList: data.pages.flatMap((page) => page.experiencesList),
      };
    },
  });

  useEffect(() => {
    for (const exp of experiences.data?.experiencesList || []) {
      queryClient.setQueryData(historyKeys.one(exp.id), {
        history: exp.history,
      });
    }
  }, [experiences.data?.experiencesList]);

  return experiences;
};
